import axios, { AxiosResponse, CancelToken } from "axios"
import { documentoComercialValidacionAdapter } from "./adapter_globals/DocumentoComercialValidacion.adapter"
import {
  albaranByFilterAdapter,
  albaranById,
  albaranByIdSimple,
  albaranDocumentoPendienteValidacionAdapter,
  albaranFicherosAdjuntosAdapter,
  albranDocAsociadosAdapter,
} from "./Albaran.adapter"
import { AlbaranCreationDtoProps } from "./DTOs/AlbaranCreationDto"
import { AlbaranReadingDtoProps } from "./DTOs/AlbaranReadingDto"
import { AlbaranReadingFiltersDtoProps } from "./DTOs/AlbaranReadingFiltersDto"
import { AlbaranReadingSimpleDtoProps } from "./DTOs/AlbaranReadingSimpleDto"
import { DocumentoComercialAsociadoReadingDtoProps } from "./DTOs/DocumentoComercialAsociadoReadingDto"
import { DocumentoComercialValidacionDtoProps } from "./DTOs/DocumentoComercialValidacionDto"
import { FicheroCreationDtoProps } from "./DTOs/FicheroCreationDto"
import { FicheroReadingDtoProps } from "./DTOs/FicheroReadingDto"
import { ValidacionDocumentoReadingDtoProps } from "./DTOs/ValidacionDocumentoReadingDto"
import { DocumentoComercialValidacionDtoQueryParamsProps } from "./interfaces/DocumentoComercialValidacionDtoQueryParamsProps"
import { SortingCriteria } from "./interfaces/SortingCriteria"
import { AlbaranModelProps } from "./Models/AlbaranModel"
import { AlbaranReadingFiltersDtoIPagedEnumerableProps } from "./Schemas/AlbaranReadingFiltersDtoIPagedEnumerable"
import { EstadoRevisionDocumentoComercialType } from "./Types/EstadoRevisionDocumentoComercial"
import { TipoDocumentoComercialType } from "./Types/TipoDocumentoComercial"

export interface ByFilterAlbaranQueryParamsProps {
  EmailLibrado?: string
  EmpresaActivaIdUsuario?: string
  EstadoRevision?: EstadoRevisionDocumentoComercialType[]
  ClienteNif?: string
  ProveedorNif?: string
  FechaEmisionLowerLimit?: string
  FechaEmisionUpperLimit?: string
  ImporteLowerLimit?: number | string
  ImporteUpperLimit?: number | string
  LibradoId?: string
  LibradorId?: string
  MaxResultCount?: number | string
  MsgEncrypted?: string
  SkipCount?: number
  SortingCriteria: SortingCriteria
  Keyword?: string
}

export async function getAlbaranByFilter(
  queryParams: ByFilterAlbaranQueryParamsProps,
  cancelToken?: CancelToken
): Promise<AlbaranReadingFiltersDtoIPagedEnumerableProps> {
  const { data }: { data: AlbaranReadingFiltersDtoIPagedEnumerableProps } =
    await axios.get("/Albaran/by-filters", {
      params: queryParams,
      cancelToken,
    })
  return {
    currentPage: data.currentPage,
    items: data.items?.map((albaran: AlbaranReadingFiltersDtoProps) =>
      albaranByFilterAdapter(albaran)
    ),
    totalCount: data.totalCount,
  }
}

/**
 * @description Check if User has any Albaran
 * @returns {Boolean}
 */
export async function getUnsignedAlbaran(
  queryParams?: DocumentoComercialValidacionDtoQueryParamsProps
): Promise<Array<DocumentoComercialValidacionDtoProps>> {
  const {
    data,
  }: Record<
    "data",
    Array<DocumentoComercialValidacionDtoProps>
  > = await axios.get("/Albaran/pendientes-firmar", {
    params: queryParams,
  })
  return data.map((documento: DocumentoComercialValidacionDtoProps) =>
    documentoComercialValidacionAdapter(documento)
  )
}

export async function postValidateAlbaranPendiente(id: string): Promise<void> {
  await axios.post(`/Albaran/${id}/validar`)
}

export async function getAlbaranPdfById(id: string) {
  const { data, status } = await axios.get(`/Albaran/${id}/pdf`, {
    responseType: "blob",
  })
  return { data, status }
}

export async function getAlbaranEdi(id: string, formato: number) {
  const { data } = await axios.get(
    `/Albaran/export-edi/${id}/formato/${formato}`,
    {
      responseType: "blob",
    }
  )
  return data
}

export async function postAlbaranPdf(documento: AlbaranModelProps) {
  const { data } = await axios.post("/Albaran/generate-pdf", documento, {
    responseType: "blob",
  })
  return data
}

export async function postAlbaran(documento: AlbaranCreationDtoProps) {
  const resp = await axios.post("/Albaran", documento)
  return resp
}

export async function getAlbaranByIdSimple(
  id: string
): Promise<AlbaranReadingSimpleDtoProps> {
  const { data } = await axios.get(`/Albaran/${id}/simple`)
  return albaranByIdSimple(data)
}

export async function getAlbaranById(
  id: string
): Promise<AlbaranReadingDtoProps> {
  const { data } = await axios.get(`/Albaran/${id}`)
  return albaranById(data)
}

export async function getAlbaranLastAvailableNumero(
  serie: string
): Promise<number> {
  const response = await axios.get(`/Albaran/last-available-numero/${serie}`)
  return response.data
}

export async function getAlbaranDocsAsociadosById(
  id: string
): Promise<DocumentoComercialAsociadoReadingDtoProps[]> {
  const { data } = await axios.get(`/Albaran/${id}/documentos-asociados`)
  return data.map((element: DocumentoComercialAsociadoReadingDtoProps) =>
    albranDocAsociadosAdapter(element)
  )
}

export async function getAlbaranFicherosAdjuntosId(
  id: string
): Promise<FicheroReadingDtoProps[]> {
  const { data } = await axios.get(`/Albaran/${id}/ficheros-adjuntos`)
  return data.map((element: FicheroReadingDtoProps) =>
    albaranFicherosAdjuntosAdapter(element)
  )
}

export async function postAlbaranGenerateExcel(queryParams: {
  tipoDocumentoComercial: TipoDocumentoComercialType
}): Promise<AxiosResponse<Blob>> {
  return axios.get("/Albaran/exportar-excel", {
    params: queryParams,
    responseType: "blob",
  })
}

export async function getAlbaranesDocumentoPendienteValidacionDocumentoPendienteValidacion(
  id: string
): Promise<ValidacionDocumentoReadingDtoProps> {
  const { data } = await axios.get(`/Albaran/${id}/validacion`)
  return albaranDocumentoPendienteValidacionAdapter(data)
}

export async function deleteAlbaranArichivoAdjunto(
  presupuestoID: string,
  fileID: string
) {
  return axios.delete(`/Albaran/${presupuestoID}/fichero/${fileID}`)
}

export async function postAlbaranAñadirArchivoAdjunto(
  presupuestoID: string,
  file: FicheroCreationDtoProps
) {
  return axios.post(`/Albaran/${presupuestoID}/add-fichero-adjunto`, file)
}
